import { BUILD_ID, APP_VERSION, VersionInfo } from './constants';

// Export constants
export { BUILD_ID, APP_VERSION };
export type { VersionInfo };

// Session storage key for one-time reload prevention
const ONE_TIME_RELOAD_KEY = 'already_reloaded_session';

export const getVersionInfo = (): VersionInfo => ({
  version: APP_VERSION,
  buildId: BUILD_ID,
  buildTime: '',
  environment: '',
  forceRefresh: false,
  lastUpdateCheck: '',
  cacheControl: {
    enabled: false,
    maxAge: 0,
    staleWhileRevalidate: 0
  }
});

export const checkNeedsUpdate = (): boolean => {
  try {
    const storedInfo = localStorage.getItem('version_info');
    if (!storedInfo) return false;

    const parsedInfo = JSON.parse(storedInfo) as VersionInfo;
    return parsedInfo.version !== APP_VERSION || parsedInfo.buildId !== BUILD_ID;
  } catch {
    return false;
  }
};

// Check if we've already done a reload in this session
export const hasReloadedThisSession = (): boolean => {
  try {
    return sessionStorage.getItem(ONE_TIME_RELOAD_KEY) === 'true';
  } catch {
    return false;
  }
};

// Mark that we've performed a reload in this session
export const markReloadedThisSession = (): void => {
  try {
    sessionStorage.setItem(ONE_TIME_RELOAD_KEY, 'true');
  } catch {
    // Ignore errors with session storage
  }
};

// Adding forceUpdate function to avoid export error
export const forceUpdate = (): void => {
  // Don't allow more than one force update per session to prevent loops
  if (hasReloadedThisSession()) {
    console.log('Already reloaded this session, preventing infinite loop');
    return;
  }
  
  // Mark that we've reloaded this session
  markReloadedThisSession();
  
  // This will import and call the actual implementation from reloadHandler
  import('./reloadHandler').then(module => {
    const currentInfo = getVersionInfo();
    module.performReload(currentInfo);
  });
};
